import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';

// Async thunk для создания жалобы
export const createReport = createAsyncThunk(
  'moderation/createReport',
  async (reportData, { rejectWithValue }) => {
    try {
      const response = await socialAPI.createReport(reportData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create report');
    }
  }
);

// Async thunk для получения жалоб (для модераторов)
export const fetchReports = createAsyncThunk(
  'moderation/fetchReports',
  async ({ status = 'pending', limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getReports({ status, limit, offset });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch reports');
    }
  }
);

// Async thunk для получения детальной информации о жалобе
export const fetchReportDetails = createAsyncThunk(
  'moderation/fetchReportDetails',
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getReportDetails(reportId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch report details');
    }
  }
);

// Async thunk для принятия решения по жалобе
export const resolveReport = createAsyncThunk(
  'moderation/resolveReport',
  async ({ reportId, decision, reason }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.resolveReport(reportId, { decision, reason });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to resolve report');
    }
  }
);

// Async thunk для блокировки контента
export const blockContent = createAsyncThunk(
  'moderation/blockContent',
  async ({ contentType, contentId, reason }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.blockContent(contentType, contentId, { reason });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to block content');
    }
  }
);

// Async thunk для разблокировки контента
export const unblockContent = createAsyncThunk(
  'moderation/unblockContent',
  async ({ contentType, contentId }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.unblockContent(contentType, contentId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unblock content');
    }
  }
);

// Async thunk для блокировки пользователя
export const blockUser = createAsyncThunk(
  'moderation/blockUser',
  async ({ userId, reason, duration }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.blockUser(userId, { reason, duration });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to block user');
    }
  }
);

// Async thunk для разблокировки пользователя
export const unblockUser = createAsyncThunk(
  'moderation/unblockUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.unblockUser(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unblock user');
    }
  }
);

// Async thunk для получения статистики модерации
export const fetchModerationStats = createAsyncThunk(
  'moderation/fetchModerationStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getModerationStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch moderation stats');
    }
  }
);

const moderationSlice = createSlice({
  name: 'moderation',
  initialState: {
    // Список жалоб
    reports: [],
    // Детальная информация о жалобах: { reportId: reportDetails }
    reportDetails: {},
    // Статистика модерации
    stats: {
      totalReports: 0,
      pendingReports: 0,
      resolvedReports: 0,
      blockedContent: 0,
      blockedUsers: 0
    },
    // Статус блокировки контента: { contentType_contentId: boolean }
    contentBlocked: {},
    // Статус блокировки пользователей: { userId: boolean }
    usersBlocked: {},
    // Общее состояние
    isLoading: false,
    error: null,
    hasMoreReports: true,
  },
  reducers: {
    // Добавление новой жалобы (приходит через WebSocket)
    addReport: (state, action) => {
      const report = action.payload;
      
      // Проверяем, нет ли уже такой жалобы
      const exists = state.reports.find(r => r.id === report.id);
      if (!exists) {
        state.reports.unshift(report);
        state.stats.totalReports += 1;
        state.stats.pendingReports += 1;
      }
    },

    // Обновление статуса жалобы (приходит через WebSocket)
    updateReportStatus: (state, action) => {
      const { reportId, status, decision } = action.payload;
      
      const reportIndex = state.reports.findIndex(r => r.id === reportId);
      if (reportIndex !== -1) {
        const oldStatus = state.reports[reportIndex].status;
        state.reports[reportIndex].status = status;
        state.reports[reportIndex].decision = decision;
        
        // Обновляем статистику
        if (oldStatus === 'pending' && status === 'resolved') {
          state.stats.pendingReports -= 1;
          state.stats.resolvedReports += 1;
        }
      }
      
      // Обновляем детальную информацию
      if (state.reportDetails[reportId]) {
        state.reportDetails[reportId].status = status;
        state.reportDetails[reportId].decision = decision;
      }
    },

    // Блокировка контента (приходит через WebSocket)
    blockContentAction: (state, action) => {
      const { contentType, contentId } = action.payload;
      const key = `${contentType}_${contentId}`;
      state.contentBlocked[key] = true;
      state.stats.blockedContent += 1;
    },

    // Разблокировка контента (приходит через WebSocket)
    unblockContentAction: (state, action) => {
      const { contentType, contentId } = action.payload;
      const key = `${contentType}_${contentId}`;
      state.contentBlocked[key] = false;
      state.stats.blockedContent -= 1;
    },

    // Блокировка пользователя (приходит через WebSocket)
    blockUserAction: (state, action) => {
      const { userId } = action.payload;
      state.usersBlocked[userId] = true;
      state.stats.blockedUsers += 1;
    },

    // Разблокировка пользователя (приходит через WebSocket)
    unblockUserAction: (state, action) => {
      const { userId } = action.payload;
      state.usersBlocked[userId] = false;
      state.stats.blockedUsers -= 1;
    },

    // Оптимистичное обновление статуса жалобы
    updateReportStatusOptimistic: (state, action) => {
      const { reportId, status } = action.payload;
      
      const reportIndex = state.reports.findIndex(r => r.id === reportId);
      if (reportIndex !== -1) {
        state.reports[reportIndex].status = status;
      }
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetModerationState: (state) => {
      state.reports = [];
      state.reportDetails = {};
      state.stats = {
        totalReports: 0,
        pendingReports: 0,
        resolvedReports: 0,
        blockedContent: 0,
        blockedUsers: 0
      };
      state.contentBlocked = {};
      state.usersBlocked = {};
      state.isLoading = false;
      state.error = null;
      state.hasMoreReports = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Создание жалобы
      .addCase(createReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createReport.fulfilled, (state, action) => {
        state.isLoading = false;
        const report = action.payload;
        
        // Добавляем в список жалоб
        state.reports.unshift(report);
        state.stats.totalReports += 1;
        state.stats.pendingReports += 1;
      })
      .addCase(createReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение жалоб
      .addCase(fetchReports.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.isLoading = false;
        const { reports, hasMore } = action.payload;
        
        // Если это первая загрузка, заменяем список
        if (state.reports.length === 0) {
          state.reports = reports;
        } else {
          // Иначе добавляем новые жалобы
          const existingIds = new Set(state.reports.map(r => r.id));
          const newReports = reports.filter(r => !existingIds.has(r.id));
          state.reports = [...state.reports, ...newReports];
        }
        
        state.hasMoreReports = hasMore;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение детальной информации о жалобе
      .addCase(fetchReportDetails.pending, (state, action) => {
        const reportId = action.meta.arg;
        state.reportDetails[reportId] = { ...state.reportDetails[reportId], isLoading: true };
      })
      .addCase(fetchReportDetails.fulfilled, (state, action) => {
        const report = action.payload;
        state.reportDetails[report.id] = { ...report, isLoading: false };
      })
      .addCase(fetchReportDetails.rejected, (state, action) => {
        const reportId = action.meta.arg;
        if (state.reportDetails[reportId]) {
          state.reportDetails[reportId].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Принятие решения по жалобе
      .addCase(resolveReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resolveReport.fulfilled, (state, action) => {
        state.isLoading = false;
        const { reportId, status, decision } = action.payload;
        
        const reportIndex = state.reports.findIndex(r => r.id === reportId);
        if (reportIndex !== -1) {
          const oldStatus = state.reports[reportIndex].status;
          state.reports[reportIndex].status = status;
          state.reports[reportIndex].decision = decision;
          
          // Обновляем статистику
          if (oldStatus === 'pending' && status === 'resolved') {
            state.stats.pendingReports -= 1;
            state.stats.resolvedReports += 1;
          }
        }
        
        // Обновляем детальную информацию
        if (state.reportDetails[reportId]) {
          state.reportDetails[reportId].status = status;
          state.reportDetails[reportId].decision = decision;
        }
      })
      .addCase(resolveReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Блокировка контента
      .addCase(blockContent.fulfilled, (state, action) => {
        const { contentType, contentId } = action.payload;
        const key = `${contentType}_${contentId}`;
        state.contentBlocked[key] = true;
        state.stats.blockedContent += 1;
      })
      .addCase(blockContent.rejected, (state, action) => {
        console.error('Failed to block content:', action.payload);
      })
      
      // Разблокировка контента
      .addCase(unblockContent.fulfilled, (state, action) => {
        const { contentType, contentId } = action.payload;
        const key = `${contentType}_${contentId}`;
        state.contentBlocked[key] = false;
        state.stats.blockedContent -= 1;
      })
      .addCase(unblockContent.rejected, (state, action) => {
        console.error('Failed to unblock content:', action.payload);
      })
      
      // Блокировка пользователя
      .addCase(blockUser.fulfilled, (state, action) => {
        const { userId } = action.payload;
        state.usersBlocked[userId] = true;
        state.stats.blockedUsers += 1;
      })
      .addCase(blockUser.rejected, (state, action) => {
        console.error('Failed to block user:', action.payload);
      })
      
      // Разблокировка пользователя
      .addCase(unblockUser.fulfilled, (state, action) => {
        const { userId } = action.payload;
        state.usersBlocked[userId] = false;
        state.stats.blockedUsers -= 1;
      })
      .addCase(unblockUser.rejected, (state, action) => {
        console.error('Failed to unblock user:', action.payload);
      })
      
      // Получение статистики модерации
      .addCase(fetchModerationStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchModerationStats.rejected, (state, action) => {
        console.error('Failed to fetch moderation stats:', action.payload);
      });
  },
});

// Экспортируем actions
export const {
  addReport,
  updateReportStatus,
  blockContentAction,
  unblockContentAction,
  blockUserAction,
  unblockUserAction,
  updateReportStatusOptimistic,
  clearError,
  resetModerationState,
} = moderationSlice.actions;

// Селекторы
export const selectReports = (state) => state.moderation.reports;
export const selectReportDetails = (state, reportId) => state.moderation.reportDetails[reportId] || null;
export const selectModerationStats = (state) => state.moderation.stats;
export const selectIsContentBlocked = (state, contentType, contentId) => {
  const key = `${contentType}_${contentId}`;
  return state.moderation.contentBlocked[key] || false;
};
export const selectIsUserBlocked = (state, userId) => state.moderation.usersBlocked[userId] || false;
export const selectModerationLoading = (state) => state.moderation.isLoading;
export const selectModerationError = (state) => state.moderation.error;
export const selectHasMoreReports = (state) => state.moderation.hasMoreReports;

export default moderationSlice.reducer;
